import { type Space, bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon20FolderOutline, Icon20MailOutline, Icon20TrashSimpleOutline } from '@vkontakte/icons';
import { Button, ButtonGroup, HorizontalScroll } from '@vkontakte/vkui';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { overquotaFoldersConfig } from 'reactApp/appHelpers/featuresHelpers/features/overquotaFolders';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { getHomeFoldersTree } from 'reactApp/modules/home/home.selectors';
import { getTrashbinSize } from 'reactApp/modules/trashbin/trashbin.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { sendDwh } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';

import styles from './OverquotaFolders.css';

interface FolderProps {
    title: string;
    id: string;
    icon: JSX.Element;
    size: number;
    space: Space | null;
}

const sendRadar = (action: string, folderCount: number) => {
    sendDwh({
        eventCategory: 'banner',
        action,
        dwh_enabled: true,
        dwhData: {
            folderCount,
            name: 'mnz_overquota_structure_space',
        },
    });
};

export const OverquotaFolders: React.FC = () => {
    const navigate = useNavigate();

    const mailQuota = useSelector(UserQuotaSelectors.getMailQuota);
    const mailFolder: FolderProps = {
        title: 'Почта',
        id: '/mail',
        icon: <Icon20MailOutline />,
        size: mailQuota ? mailQuota.original : 0,
        space: mailQuota,
    };

    const trashSize = useSelector(getTrashbinSize);
    const trashFolder: FolderProps = {
        title: 'Корзина',
        id: '/trashbin',
        icon: <Icon20TrashSimpleOutline />,
        size: trashSize,
        space: bytesToNDigits(trashSize, 3),
    };

    const homeFolders: FolderProps[] = useSelector(getHomeFoldersTree).map((folder) => ({
        ...folder,
        icon: <Icon20FolderOutline />,
        size: folder?.size || 0,
        space: bytesToNDigits(folder?.size || 0, 3),
    }));

    const folders: FolderProps[] = [...homeFolders, mailFolder, trashFolder]
        .filter((item) => item.size > 0)
        .sort((a, b) => b.size - a.size)
        .slice(0, overquotaFoldersConfig.count);

    const onClick = useCallback((id: string) => {
        sendRadar('click-cleaner', folders.length);

        if (id === '/mail') {
            opener(overquotaFoldersConfig.url);
        } else if (id === '/trashbin') {
            navigate({ pathname: id });
        } else {
            navigate({ pathname: `/home${id}` });
            toolbarActions.view('list');
        }
    }, []);

    useEffect(() => {
        sendRadar('show', folders.length);
    }, []);

    return (
        <div style={{ overflow: 'clip' }}>
            <HorizontalScroll scrollOnAnyWheel showArrows={false} data-test-id={'overquota-bnr-flrds-scroll'}>
                <ButtonGroup mode={'horizontal'} gap={'none'} className={styles.overquotaFolders}>
                    {folders.map((folder: FolderProps, index) => (
                        <div key={folder.id} className={styles.buttonWrapper}>
                            <Button
                                before={folder.icon}
                                after={<span className={styles.red}>{folder.space?.value}</span>}
                                appearance={'neutral'}
                                mode={'tertiary'}
                                size={'l'}
                                onClick={() => onClick(folder.id)}
                                data-test-id={`overquota-bnr-fldr-${folder.id}`}
                                className={styles.button}
                            >
                                {folder.title}
                            </Button>
                            {index !== folders.length - 1 && <div className={styles.separator} />}
                        </div>
                    ))}
                </ButtonGroup>
            </HorizontalScroll>
        </div>
    );
};
